import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DemoSearch, Dropdown, Input, PopupSelectCar } from '../../../../components';
import { validateVin } from '../../../../shared';
import { Brand, BrandField, BrandFieldValues, CarInfo } from '../../../../types';
import { HintText, Text } from '../../style';
import { DemoVin, User } from '../../../../../../types';

interface Props {
  vin?: string;
  brandId?: number;
  brandsList?: Brand[];
  brandFields?: BrandFieldValues;
  brandFieldsList?: BrandField[];
  vinNotFound?: boolean;
  showSearchButton?: boolean;
  carsToSelect?: CarInfo[];
  carId?: number;
  user?: User;
  demoVINs?: DemoVin[];
  setVin: (value: string) => void;
  searchByVin: (vin?: string, callbackError?: () => void) => void;
  searchByParams: () => void;
  setBrandId: (value?: number) => void;
  getBrandsList: () => void;
  clearBrandFields: () => void;
  getBrandFields: (brandId: string) => void;
  setBrandField: (fieldId: number, optionId: number | null, fieldName: string, fieldValue: string, callback: (value: boolean) => void) => void;
  setVinNotFound: (value: boolean) => void;
  setShowSearchButton: (value: boolean) => void;
  clearBrandData: () => void;
  setCarInfo: (car: CarInfo) => void;
  setCarId: (value: number) => void;
}

export const Search = ({ vin, brandId, brandsList, brandFields, brandFieldsList, vinNotFound, showSearchButton, carsToSelect, carId, user, demoVINs, setVin, searchByVin, searchByParams,
  getBrandsList, setBrandId, clearBrandFields, getBrandFields, setBrandField, setVinNotFound, setShowSearchButton, clearBrandData, setCarInfo, setCarId }: Props) => {

  const { t } = useTranslation();

  const [vinError, setVinError] = useState<string | undefined>();
  const [showSelectCarPopup, setShowSelectCarPopup] = useState(false);
  const [showDemoConfirmation, setShowDemoConfirmation] = useState(false);

  const hintText = useMemo(() => !vinNotFound ?
    `Your vehicle's VIN/Frame is the most reliable identifier. If you do not know the VIN/Frame number, then use the search by parameters, which is located below` :
    `Can't find car by VIN/Frame, please, use search by parameters`, [vinNotFound]);

  useEffect(() => {
    if ((brandsList?.length || 0) === 0) {
      getBrandsList();
    }
  }, [brandsList, getBrandsList]);

  // eslint-disable-next-line unicorn/no-useless-undefined
  useEffect(() => setVinError(undefined), [vin]);

  useEffect(() => { setVinNotFound(false); }, [vin, setVinNotFound]);

  useEffect(() => {
    if (carsToSelect && carId === undefined) {
      setShowSelectCarPopup(true);
    }
  }, [carsToSelect, carId]);

  const changeBrand = useCallback((value?: number) => {
    setShowSearchButton(false);

    if (value === undefined) {
      clearBrandFields();
      return;
    }

    getBrandFields(value.toString());
  }, [clearBrandFields, getBrandFields, setShowSearchButton]);

  return <>
    {showDemoConfirmation && <DemoSearch
      onSelect={(value: string) => {
        setVin(value);
        searchByVin(value, () => setVinNotFound(true));
        setShowDemoConfirmation(false);
      }}
      onClose={() => setShowDemoConfirmation(false)}
      demoVINs={demoVINs}
    />}
    {showSelectCarPopup && carsToSelect && <PopupSelectCar
      cars={carsToSelect}
      onClose={() => setShowSelectCarPopup(false)}
      onSelect={(value) => {
        setCarInfo(carsToSelect[value]);
        setCarId(value);
        setShowSelectCarPopup(false);
      }}
    />}
    <Text marginBottom={24} onClick={demoVINs ? () => setVin(demoVINs[Math.floor(Math.random() * demoVINs.length)].vin) : undefined}>{t('Enter the VIN/Frame to identify the vehicle')}</Text>
    <Input
      width='600px'
      placeholder='VIN/Frame number'
      inputPlaceholder='4F2YU08102KM26251'
      value={vin}
      onChange={(value) => setVin(value)}
      error={vinError || (vinNotFound ? '' : undefined)}
      errorIcon={vinNotFound}
      icon='search'
    />
    {((vin === '' && !brandId) || !!vin) && <>
      <HintText marginTop={24} marginBottom={24} width={600} height={48} error={vinNotFound}>{t(hintText)}</HintText>
      <Button width='178px' disabled={!vin} onClick={() => {
        if (!user && !demoVINs?.find((item) => item.vin === vin)) {
          setShowDemoConfirmation(true);
        } else if (validateVin(vin)) {
          searchByVin(vin, () => setVinNotFound(true));
          clearBrandData();
        } else {
          setVinError('VIN should contain latin characters and numbers');
        }
      }}>Search</Button>
    </>}
    <>
      <Text marginTop={48} marginBottom={24}>{t('Or identify the vehicle by the parameters')}</Text>
      {brandsList && <Dropdown
        width='600px'
        placeholder='Brand'
        inputPlaceholder='Choose your car brand'
        value={brandId?.toString() || ''}
        options={brandsList?.map(({ id, name }) => ({ value: id.toString(), label: name }))}
        maxHeight={358}
        onChange={(value) => {
          setBrandId(value ? +value : undefined);
          changeBrand(value ? +value : undefined);
        }}
        search
      />}
      {brandFieldsList?.map((item) => <Dropdown
        // disabled={!!vin}
        width='600px'
        margin='8px 0 0 0'
        key={`${brandId} ${item.field}`}
        placeholder={item.field}
        inputPlaceholder={`Choose your ${item.field.toLowerCase()}`}
        options={item.options.map(({ id, name }) => ({ value: id.toString(), label: name }))}
        value={item.options.find((option) => option.name === brandFields?.[item.field]) ?
          `${item.options.find((option) => option.name === brandFields?.[item.field])?.id}` : ''}
        onChange={(value) => value === undefined ? undefined : (() => {
          const optionId = value === "" ? null : +value;
          const fieldValue = item.options.find(({ id }) => id === optionId)?.name || '';
          setBrandField(item.id, optionId, item.field, fieldValue, (allowListVehicles) => setShowSearchButton(allowListVehicles));
        })()}
        search
      />)}
      {showSearchButton && <Button width='178px' onClick={user ? () => searchByParams() : () => setShowDemoConfirmation(true)} margin='20px 0 0'>Search</Button>}
    </>
  </>;
};
